"use client";
import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Divide } from 'lucide-react';
import ProductCard from './ProductCard';
import { useNavigate } from 'react-router-dom';
import { useTrending } from '../context/TrendingContext';
import { UPLOAD_URL } from '../config/config';

function TrendingProductGrid() {
  const navigate = useNavigate();
  const { products, loading, error } = useTrending();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsPerView, setItemsPerView] = useState(5);

  const handleProductsClick = () => {
    navigate('/products');
  }

  // Handle product click navigation
  const handleProductClick = (productId) => {
    navigate(`/products/${productId}`);
  };

  // Get responsive items per view
  const getItemsPerView = () => {
    if (typeof window !== 'undefined') {
      if (window.innerWidth < 768) return 1; // mobile
      if (window.innerWidth < 1024) return 2; // tablet
      return 5; // desktop
    }
    return 5;
  };

  useEffect(() => {
    const updateItemsPerView = () => {
      const newItemsPerView = getItemsPerView();
      setItemsPerView(newItemsPerView);
      // Reset to valid index when screen size changes
      setCurrentIndex(0);
    };

    updateItemsPerView();
    window.addEventListener('resize', updateItemsPerView);
    return () => window.removeEventListener('resize', updateItemsPerView);
  }, []);

  // Calculate max index - how many slides we can move
  const maxIndex = Math.max(0, products.length - itemsPerView);

  const nextSlide = () => {
    setCurrentIndex((prev) => {
      const next = prev + 1;
      return next > maxIndex ? maxIndex : next;
    });
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => {
      const next = prev - 1;
      return next < 0 ? 0 : next;
    });
  };

  // Calculate transform percentage
  const getTransformValue = () => {
    return -(currentIndex * (100 / itemsPerView));
  };

  return (
    <section className="w-full max-w-[90%] mx-auto mt-2.5 px-4 max-md:px-2">
      <div className="flex flex-col gap-4 items-center pt-8 w-full">
        <div className="flex items-center justify-between w-full max-w-[1400px]">
          <div className="w-20"></div>
          <header className="flex flex-col items-center text-xl font-medium text-center text-neutral-700">
            <h2 className="font-medium text-neutral-700">Trending Now</h2>
          </header>
          <div className="flex gap-2 w-20 justify-end">
            <button
              onClick={prevSlide}
              disabled={currentIndex === 0}
              className="p-2 rounded-xl hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Previous products"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={nextSlide}
              disabled={currentIndex >= maxIndex}
              className="p-2 rounded-xl hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Next products"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>

        <div className="w-full mt-4">
          <div className="overflow-hidden w-full">
            <div
              className="flex transition-transform duration-300 ease-in-out"
              style={{
                transform: `translateX(${getTransformValue()}%)`,
              }}
            >
              {products.map((product, index) => {
                const imgUrl = product.image
                  ? `${UPLOAD_URL}/products/${product.image}`
                  : 'fallback-image-url';

                const variantKey = [product.id].join("|");

                const productData = {
                  id: product.id,
                  key: variantKey,                       // ✅ unique per variant
                  name: product.name,
                  image: imgUrl,
                  category: product.category || "Uncategorized",
                  color: product.colors || "N/A",
                  fabric: product.fabrics || "N/A",
                  price: Number(product.price) || 0,
                  offer_price: Number(product.offer_price) || 0,
                  quantity:1,
                };

                // console.log("Rendering ProductCard:", productData);

                
                return (
                  <div
                    key={product.id}
                    className="flex-none px-2"
                    style={{
                      width: `${100 / itemsPerView}%`,
                    }}
                  >
                    <ProductCard
                      product={productData}
                      id={product.id}
                      imgUrl={imgUrl}
                      title={product.name}
                      price={productData.price}            
                      offer_price={productData.offer_price}  
                      hasWishlist={false}
                      onClick={() => handleProductClick(product.id)}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Dots indicator */}
        <div className="flex gap-2 mt-4">
          {Array.from({ length: maxIndex + 1 }).map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`w-2 h-2 rounded-full transition-colors ${
                currentIndex === index ? 'bg-yellow-500' : 'bg-gray-300'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
        {/* shadow-[0px_4px_4px_rgba(0,0,0,0.25)] */}
        <button className="inline-block px-10 py-4 mt-2 text-xl lg:text-2xl rounded-l  text-center border-2 border-solid bg-transparent border-black  hover:bg-black hover:text-white transition-colors max-md:px-4 max-md:py-3 max-md:text-lg max-md:mt-4 whitespace-nowrap"
             onClick={()=>navigate('/products')}>
              Shop All Products
        </button>
      </div>
    </section>
  );
}

export default TrendingProductGrid;
